import {useState} from 'react';
import './ReviewStep.scss';
import {t} from 'i18next';
import {Box, Tooltip, Typography} from '@mui/material';
import ArrButton from '../../../../common/arr-button/ArrButton';
import ArrTextField from '../../../../common/arr-text-field/ArrTextField';
import {normalizeEmail} from '../../../../ai/investigate/helpers/aihelper';
import {ReactComponent as DeleteIcon} from '../../../../../assets/images/delete.svg';
import ArrIconButton from '../../../../common/arr-icon-button/ArrIconButton';
import {ArrDrawer} from '../../../../common/arr-drawer/ArrDrawer';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import {
  ISecurityGroupCountResponse,
  ToastMessageConfigType,
} from '../../../../../api/types/ingestionApi.type';
import {MAX_EMAIL_LENGTH, ToastType} from '../../../../../shared/constants';
import {useDispatch, useSelector} from 'react-redux';
import {setNotificationEmails} from '../../../../../redux/slice/createCustomIndexSlice';
import {RootState} from '../../../../../redux/store';
import TruncatedTooltip from '../../../../document-viewer/doc-list/truncated-tooltip/TruncatedTooltip';

const validateReviewEmail = (email: string) => {
  const normalizedEmail = normalizeEmail(email);
  if (normalizedEmail.length > MAX_EMAIL_LENGTH) {
    return `Email address cannot exceed ${MAX_EMAIL_LENGTH} characters.`;
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
    return t('ai.investigate.createEditIndex.create.emailError');
  }
  return null;
};

const ReviewStep = () => {
  const dispatch = useDispatch();

  const emails = useSelector(
    (state: RootState) =>
      state.indexManagement?.createCustomIndex?.notificationEmails || []
  );
  const {isReadOnlyView, securityGroupCount} = useSelector(
    (state: RootState) => state.indexManagement
  );

  const selectedSource = useSelector(
    (state: RootState) => state.indexManagement?.selectedDocuments || ''
  );

  const selectedDocuments = useSelector(
    (state: RootState) => state.indexManagement?.selectedDocuments || []
  );

  const selectedFields = useSelector(
    (state: RootState) =>
      state.indexManagement?.selectedMetadataFieldNames || []
  );

  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailInput, setEmailInput] = useState('');
  const [openPanel, setOpenPanel] = useState<boolean>(false);
  const [toastMsgConfig, setToastMsgConfig] = useState<
    ToastMessageConfigType & {show: boolean}
  >({
    show: false,
  });

  const addEmail = () => {
    const trimmed = emailInput.trim();
    if (!trimmed) return;

    const err = validateReviewEmail(trimmed);
    if (err) {
      setEmailError(err);
      return;
    }

    const normalized = normalizeEmail(trimmed);

    const updated = [
      ...emails?.filter((e: any) => e !== normalized),
      normalized,
    ];

    dispatch(setNotificationEmails(updated));

    setEmailInput('');
    setEmailError(null);
  };

  const removeEmail = (email: string) => {
    const updated = emails.filter((e: any) => e !== email);
    dispatch(setNotificationEmails(updated));
  };

  const customIndexName = useSelector(
    (state: RootState) => state.indexManagement.createCustomIndex.indexName
  );

  const sortedFields = [...selectedFields].sort((a, b) =>
    a.fieldName.localeCompare(b.fieldName)
  );

  const firstFourFields = sortedFields.slice(0, 4);

  return (
    <Box className="review-wrapper">
      <Box className="review-card">
        <Box className="review-header">
          <Typography
            className="typo-text subhead2 review-title"
            aria-label={t('indexManagement.customIndex.review.header')}
            aria-live="polite"
            aria-atomic="false"
            data-testid={`review-step-header`}
            tabIndex={0}
          >
            {t('indexManagement.customIndex.review.header')}
          </Typography>
        </Box>

        <Box className="review-grid">
          <Box className="left-section">
            <Box className="field-block">
              <Typography
                className="typo-text body2_size label"
                aria-label={t('indexManagement.customIndex.review.indexName')}
                aria-live="polite"
                aria-atomic="false"
                data-testid={`review-step-index-name`}
                tabIndex={0}
              >
                {t('indexManagement.customIndex.review.indexName')}
              </Typography>
              <Box sx={{flex: 1, minWidth: 0}}>
                <TruncatedTooltip
                  content={customIndexName}
                  className="typo-text subhead4_size value"
                  dataTestId="review-step-my-custom-index"
                />
              </Box>
            </Box>

            <Box className="field-block padding-top">
              <Typography
                className="typo-text body2_size label"
                aria-label={t('indexManagement.customIndex.review.source')}
                aria-live="polite"
                aria-atomic="false"
                data-testid={`review-step-source`}
                tabIndex={0}
              >
                {t('indexManagement.customIndex.review.source')}
              </Typography>
              <Box sx={{flex: 1, minWidth: 0}}>
                <TruncatedTooltip
                  content={selectedSource?.source}
                  className="typo-text subhead4_size value"
                  dataTestId="review-step-saved-search4823719"
                />
              </Box>
            </Box>
            <Box className="field-block-sub padding-top">
              <Box className="field-block-part">
                <Typography
                  className="typo-text body2_size label"
                  aria-label={t('indexManagement.customIndex.review.docs')}
                  aria-live="polite"
                  aria-atomic="false"
                  data-testid={`review-step-docs-label`}
                  tabIndex={0}
                >
                  {t('indexManagement.customIndex.review.documents')}
                </Typography>
                <Typography
                  className="typo-text subhead4_size link"
                  aria-label={t('indexManagement.customIndex.review.docs')}
                  aria-live="polite"
                  aria-atomic="false"
                  data-testid={`review-step-docs-value`}
                  tabIndex={0}
                >
                  {selectedDocuments?.documents}{' '}
                  {t('indexManagement.customIndex.review.docs')}
                </Typography>
              </Box>
              <Box className="padding-bottom">
                {securityGroupCount?.map(
                  (group: ISecurityGroupCountResponse, index: number) => (
                    <Box className="field-block-part">
                      <Typography
                        className="typo-text body4_size label"
                        aria-label={t(
                          'indexManagement.customIndex.review.secGroup1'
                        )}
                        aria-live="polite"
                        aria-atomic="false"
                        data-testid={`review-step-sec-group1-${index}`}
                        tabIndex={0}
                      >
                        {group?.securityGroupName}:
                      </Typography>
                      <Typography
                        className="typo-text label3_size value"
                        aria-label={t(
                          'indexManagement.customIndex.review.secGroup1'
                        )}
                        aria-live="polite"
                        aria-atomic="false"
                        data-testid={`review-step-sec-group1-value-${index}`}
                        tabIndex={0}
                      >
                        {group?.documentCount}
                      </Typography>
                    </Box>
                  )
                )}
              </Box>
            </Box>
            <Box className="field-block padding-top">
              <Typography
                className="typo-text body2_size label"
                aria-label={t('indexManagement.customIndex.review.secGroup')}
                aria-live="polite"
                aria-atomic="false"
                data-testid={`review-step-sec-group`}
                tabIndex={0}
              >
                {t('indexManagement.customIndex.review.secGroup')}
              </Typography>
              <Box className="security-group-field">
                {securityGroupCount?.length ? (
                  securityGroupCount.map((securityGroup) => {
                    return (
                      <Box className="security-group-list">
                        <Typography
                          className="typo-text subhead4_size"
                          data-testId={`review-step-selected-security-group`}
                          aria-label={securityGroup?.securityGroupName}
                        >
                          {securityGroup?.securityGroupName}
                        </Typography>
                        <Typography
                          component="span"
                          className="typo-text body3_size userlabel"
                          aria-label={t(
                            'indexManagement.customIndex.review.secGroup2'
                          )}
                          aria-live="polite"
                          aria-atomic="false"
                          data-testid={`review-step-count-users`}
                          tabIndex={0}
                        >
                          ({securityGroup?.userCount}{' '}
                          {securityGroup?.userCount > 1 ? 'users' : 'user'})
                        </Typography>
                      </Box>
                    );
                  })
                ) : (
                  <Typography
                    className="typo-text subhead4_size"
                    data-testid="review-step-selected-security-group"
                    aria-label="---"
                    tabIndex={0}
                  >
                    ---
                  </Typography>
                )}
              </Box>
            </Box>

            <Box className="field-block padding-top no-border">
              <Typography
                className="typo-text body2_size label"
                aria-label={t('indexManagement.customIndex.review.fields')}
                aria-live="polite"
                aria-atomic="false"
                data-testid={`review-step-fields`}
                tabIndex={0}
              >
                {t('indexManagement.customIndex.review.fields')}
              </Typography>
              <Box className="fields-list">
                {firstFourFields.map((f, i) => (
                  <Typography
                    key={'field' + i}
                    className="typo-text subhead4_size value"
                    aria-label={f.fieldName}
                    aria-live="polite"
                    aria-atomic="false"
                    data-testid={`review-step-field-${i}`}
                    tabIndex={0}
                  >
                    {f.fieldName}
                  </Typography>
                ))}
                <Box className="filed-button">
                  <ArrButton
                    variant="text"
                    aria-label={t('indexManagement.customIndex.review.showAll')}
                    aria-live="polite"
                    aria-atomic="false"
                    size="small"
                    onClick={() => setOpenPanel(true)}
                    dataTestId="review-step-show-all-button"
                  >
                    {t('indexManagement.customIndex.review.showAll')}
                  </ArrButton>
                </Box>
              </Box>
            </Box>
          </Box>

          {!isReadOnlyView && (
            <Box className="right-section">
              <Box className="email-block">
                <Box className="email-head">
                  <Typography
                    component="span"
                    aria-label={t(
                      'ai.investigate.createEditIndex.create.emailHeader'
                    )}
                    aria-live="polite"
                    aria-atomic="false"
                    className="typo-text subhead3_size"
                    data-testid={`review-step-email-header`}
                    tabIndex={0}
                  >
                    {t('ai.investigate.createEditIndex.create.emailHeader')}
                  </Typography>
                  <Typography
                    component="span"
                    aria-label={t(
                      'ai.investigate.createEditIndex.create.emailOptional'
                    )}
                    aria-live="polite"
                    aria-atomic="false"
                    className="typo-text subhead3_size label"
                    data-testid={`review-step-optional-text`}
                    tabIndex={0}
                  >
                    {' ' +
                      t('ai.investigate.createEditIndex.create.emailOptional')}
                  </Typography>
                </Box>
                <Box className="email-subhead">
                  <Typography
                    component="span"
                    aria-label={t(
                      'ai.investigate.createEditIndex.create.emailSubHeader'
                    )}
                    aria-live="polite"
                    aria-atomic="false"
                    className="typo-text body2_size"
                    data-testid={`review-step-email-sub-header`}
                    tabIndex={0}
                  >
                    {t('ai.investigate.createEditIndex.create.emailSubHeader')}
                  </Typography>
                </Box>
                <Box className="email-row">
                  <ArrTextField
                    className="email-text-field"
                    id="enabled-focus"
                    state={emailError ? 'error' : 'enabled'}
                    aria-label={t(
                      'ai.investigate.createEditIndex.create.emailAddress'
                    )}
                    aria-live="polite"
                    aria-atomic="false"
                    label={t(
                      'ai.investigate.createEditIndex.create.emailAddress'
                    )}
                    dataTestId="review-step-email"
                    supportText={emailError || ''}
                    value={emailInput}
                    isAriaDisabled={emails.length >= 10}
                    disabled={emails.length >= 10}
                    onChange={(e) => {
                      const val = e.target.value;
                      setEmailInput(val);
                      if (!val.trim()) {
                        setEmailError(null);
                        return;
                      }
                      if (val.length > MAX_EMAIL_LENGTH) {
                        setEmailError(
                          `Email address cannot exceed ${MAX_EMAIL_LENGTH} characters.`
                        );
                        return;
                      }
                      const err = validateReviewEmail(val.trim());
                      if (err) {
                        setEmailError(err);
                      } else {
                        setEmailError(null);
                      }
                    }}
                    showErrorIcon
                  />

                  <ArrButton
                    variant="text"
                    aria-label={t('common.add')}
                    aria-live="polite"
                    aria-atomic="false"
                    size="large"
                    onClick={addEmail}
                    aria-disabled={
                      !emailInput.trim() || !!emailError || emails.length >= 10
                    }
                    disabled={
                      !emailInput.trim() || !!emailError || emails.length >= 10
                    }
                    dataTestId="review-step-add-button"
                  >
                    {t('common.add')}
                  </ArrButton>
                </Box>

                <Box className="email-list">
                  {emails?.map((email: any, index: any) => (
                    <Box className="email-item" key={email}>
                      <Tooltip
                        title={
                          <Typography
                            component="span"
                            className="email-tooltip-text"
                            tabIndex={0}
                          >
                            {email}
                          </Typography>
                        }
                        arrow
                        classes={{
                          tooltip: 'email-tooltip',
                          arrow: 'email-tooltip-arrow',
                        }}
                      >
                        <Typography
                          className="typo-text body2_size email-tooltip-trigger email-value"
                          aria-label={email}
                          aria-live="polite"
                          aria-atomic="false"
                          data-testid={`review-step-email-${index}`}
                          tabIndex={0}
                        >
                          {email}
                        </Typography>
                      </Tooltip>
                      <ArrIconButton
                        testId={`arr-delete-icon`}
                        aria-label={'Delete'}
                        aria-live="polite"
                        aria-atomic="false"
                        onClick={() => removeEmail(email)}
                        icon={<DeleteIcon />}
                        ariaLabel={'delete-icon'}
                        shape="none"
                      ></ArrIconButton>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
        {!isReadOnlyView && (
          <Box className="footer-content">
            <Box className="footer-banner">
              <Typography
                className="typo-text body3_size footer-text"
                aria-label={t('indexManagement.customIndex.review.footer')}
                aria-live="polite"
                aria-atomic="false"
                data-testid={`review-step-footer`}
                tabIndex={0}
              >
                {t('indexManagement.customIndex.review.footer')}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
      {openPanel && (
        <ArrDrawer
          aria-label={t(
            'ai.investigate.createEditIndex.create.createIndexTitle'
          )}
          aria-live="polite"
          aria-atomic="false"
          isOpen={openPanel}
          onClose={() => setOpenPanel(false)}
          showButton={false}
          showHeader={false}
          title={t('ai.investigate.createEditIndex.create.createIndexTitle')}
          size="small"
          side="right"
          data-testid="createindex-drawer"
        >
          <Box className="fields-drawer-container">
            <Box className="fields-drawer-header-box">
              <Box className="fields-drawer-row">
                <Typography
                  component="h3"
                  role="header"
                  aria-label={t('indexManagement.customIndex.review.fields')}
                  aria-live="polite"
                  aria-atomic="false"
                  className="typo-text headline3_size"
                  data-testid={`review-step-fields-text`}
                  tabIndex={0}
                >
                  {t('indexManagement.customIndex.review.fields')}
                </Typography>
                <Box className="fields-drawer-buttons-box">
                  <ArrButton
                    aria-label={t('common.close')}
                    aria-live="polite"
                    aria-atomic="false"
                    variant="secondary"
                    size="medium"
                    onClick={() => setOpenPanel(false)}
                    disabled={false}
                    dataTestId="review-step-close-button"
                    className="close-button"
                  >
                    {t('common.close')}
                  </ArrButton>
                </Box>
              </Box>
            </Box>
            <Box className="fields-drawer-body">
              <Box className="table-header">
                <Typography
                  className="typo-text label2_size"
                  aria-label={t('indexManagement.customIndex.review.fieldName')}
                  aria-live="polite"
                  aria-atomic="false"
                  data-testid={`review-step-field-name`}
                  tabIndex={0}
                >
                  {t('indexManagement.customIndex.review.fieldName')}
                </Typography>
                <Typography
                  className="typo-text label2_size"
                  aria-label={t('indexManagement.customIndex.review.analyzer')}
                  aria-live="polite"
                  aria-atomic="false"
                  data-testid={`review-step-analyzer`}
                  tabIndex={0}
                >
                  {t('indexManagement.customIndex.review.analyzer')}
                </Typography>
              </Box>
              <Box className="table-content">
                {sortedFields.map((f, i) => (
                  <Box key={'fields' + i} className="field-row-item">
                    <Typography
                      className="typo-text body3_size"
                      aria-label={f.fieldName}
                      aria-live="polite"
                      aria-atomic="false"
                      data-testid={`review-step-sorted-field-${i}`}
                      tabIndex={0}
                    >
                      {f.fieldName}
                    </Typography>
                    <Typography
                      className="typo-text body3_size"
                      aria-label={f.analyzerName ?? f.analyzerKey}
                      aria-live="polite"
                      aria-atomic="false"
                      data-testid={`review-step-analyzer-${i}`}
                      tabIndex={0}
                    >
                      {f.analyzerName ?? f.analyzerKey}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </ArrDrawer>
      )}
      {toastMsgConfig.show && (
        <ArrToastNotification
          onClose={() => setToastMsgConfig({show: false})}
          status={[toastMsgConfig.type ?? ToastType.Success]}
          message={toastMsgConfig.message ?? ''}
          title={toastMsgConfig.title}
        />
      )}
    </Box>
  );
};

export default ReviewStep;
